import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { SELECTED_ROW_MARKER } from './browserStyles';
import { DetailsPane } from './DetailsPane';
import { DetailsPaneCloseContext } from './detailsPaneClose';

function findSelectedRow(scope: HTMLElement | null): HTMLElement | null {
    return scope?.querySelector<HTMLElement>(`.${SELECTED_ROW_MARKER}`) ?? null;
}

interface BrowseBodyProps {
    list: ReactNode;
    details: ReactNode;
    detailsLabel: string;
    // Key of the selected row while it is in the table; null keeps the pane closed.
    openKey: string | null;
    onCloseDetails: () => void;
}

export function BrowseBody({
    list,
    details,
    detailsLabel,
    openKey,
    onCloseDetails
}: BrowseBodyProps) {
    const scopeRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (openKey !== null)
            findSelectedRow(scopeRef.current)?.scrollIntoView({ block: 'nearest' });
    }, [openKey]);

    // The row is looked up before the selection clears, while it still carries the marker.
    const closeDetails = useCallback(() => {
        const row = findSelectedRow(scopeRef.current);
        onCloseDetails();
        row?.focus();
    }, [onCloseDetails]);

    return (
        <DetailsPaneCloseContext.Provider value={closeDetails}>
            <div ref={scopeRef} className="flex min-h-0 flex-1">
                <div className="flex min-h-0 min-w-0 flex-1 flex-col">{list}</div>
                {openKey !== null && (
                    <DetailsPane
                        label={detailsLabel}
                        escapeScopeRef={scopeRef}
                        onClose={closeDetails}
                    >
                        {details}
                    </DetailsPane>
                )}
            </div>
        </DetailsPaneCloseContext.Provider>
    );
}
